import React, { useState, useEffect } from 'react';
import { theme } from './theme';
import Dashboard from './components/Dashboard';
import Login from './components/Login';
import UserMenu from './components/UserMenu';
import Logo from './components/Logo';
import config from './config';

function App() {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [activeDashboard, setActiveDashboard] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [currentDashboardUrl, setCurrentDashboardUrl] = useState(null);
  const [isThemeInitialized, setIsThemeInitialized] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [lastFetchSuccessful, setLastFetchSuccessful] = useState(false);
  const [lastSuccessfulDashboard, setLastSuccessfulDashboard] = useState(null);
  const [menuItems, setMenuItems] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      setIsDarkMode(savedTheme === 'dark');
    }
    setIsThemeInitialized(true);
  }, []);

  useEffect(() => {
    if (!isThemeInitialized) return;

    const currentTheme = isDarkMode ? 'dark' : 'light';
    localStorage.setItem('theme', currentTheme);
    document.documentElement.setAttribute('data-theme', currentTheme);
    
    // Apply theme variables
    const themeVars = isDarkMode ? theme.dark : theme.light;
    Object.entries(themeVars).forEach(([key, value]) => {
      document.documentElement.style.setProperty(`--${key}`, value);
    });

    // Gọi lại API với dashboard thành công gần nhất
    if (lastSuccessfulDashboard) {
      fetchDashboardUrl(lastSuccessfulDashboard);
    }
  }, [isDarkMode, isThemeInitialized]);

  const toggleTheme = () => {
    setIsDarkMode(prev => !prev);
  };

  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
  };

  useEffect(() => {
    const handleKeyPress = (e) => {
      // Toggle sidebar với Ctrl + B
      if (e.ctrlKey && e.key === 'b') {
        e.preventDefault();
        toggleSidebar();
      }
      // Toggle theme với Ctrl + T
      if (e.ctrlKey && e.key === 't') {
        e.preventDefault();
        toggleTheme();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);

  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem('authToken');
      const savedUserInfo = localStorage.getItem('userInfo');

      if (!token) {
        setIsLoading(false);
        return;
      }

      if (savedUserInfo) {
        setUserInfo(JSON.parse(savedUserInfo));
        setIsAuthenticated(true);
        setIsLoading(false);
        return;
      }

      try {
        const response = await fetch(`${config.API_URL}${config.endpoints.auth.login}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          setUserInfo(data);
          localStorage.setItem('userInfo', JSON.stringify(data));
          setIsAuthenticated(true);
        } else {
          throw new Error('Failed to fetch user info');
        }
      } catch (error) {
        console.error('Auth error:', error);
        localStorage.removeItem('authToken');
        localStorage.removeItem('userInfo');
        setIsAuthenticated(false);
        setUserInfo(null);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []); // Chỉ chạy một lần khi component mount

  const handleLogin = async (token) => {
    try {
      const loginTime = Date.now(); // Lưu thời gian đăng nhập
      localStorage.setItem('authToken', token);
      localStorage.setItem('loginTime', loginTime); // Lưu thời gian đăng nhập

      const response = await fetch(`${config.API_URL}${config.endpoints.auth.login}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setUserInfo(data);
        localStorage.setItem('userInfo', JSON.stringify(data));
        setIsAuthenticated(true);
      } else {
        throw new Error('Failed to fetch user info');
      }
    } catch (error) {
      console.error('Login error:', error);
      localStorage.removeItem('authToken');
      localStorage.removeItem('userInfo');
      localStorage.removeItem('loginTime'); // Xóa loginTime nếu có lỗi
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userInfo');
    localStorage.removeItem('loginTime');
    setIsAuthenticated(false);
    setUserInfo(null);
  };

  const fetchDashboardUrl = async (dashboardId) => {
    try {
      setErrorMessage(null);
      const token = localStorage.getItem('authToken');
      const currentTheme = isDarkMode ? 'dark' : 'light';
      
      const response = await fetch(
        `${config.API_URL}${config.endpoints.report.getDashboardUrl}?dashboard=${dashboardId}&ui_mode=${currentTheme}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      const data = await response.json();
      
      if (data.errorCode === "1" && data.data) {
        setCurrentDashboardUrl(data.data);
        setLastSuccessfulDashboard(dashboardId);
        setLastFetchSuccessful(true);
        return data.data;
      } else {
        setErrorMessage(data.errorMessage);
        setLastFetchSuccessful(false);
        return null;
      }
    } catch (error) {
      console.error('Error fetching dashboard URL:', error);
      setErrorMessage('Failed to fetch dashboard URL');
      setLastFetchSuccessful(false);
      return null;
    }
  };

  const handleDashboardClick = async (dashboardId) => {
    const result = await fetchDashboardUrl(dashboardId);
    if (result) {
      setActiveDashboard(dashboardId);
    }
  };

  const handleLogoClick = () => {
    setActiveDashboard(null);
    setCurrentDashboardUrl(null);
  };

  const Welcome = () => {
    return (
      <div className="welcome-text">
        {'Welcome!'.split('').map((char, i) => (
          <span key={i} style={{ '--i': i }}>
            {char}
          </span>
        ))}
      </div>
    );
  };

  const handleCloseError = () => {
    setErrorMessage(null);
  };

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (errorMessage && (e.key === 'Enter' || e.key === 'Escape')) {
        handleCloseError();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [errorMessage]);

  const getDashboardFullName = (dashboardId) => {
    for (const [category, items] of Object.entries(menuItems)) {
      const item = items.find(item => item.dashboardId === dashboardId);
      if (item) return `${category} - ${item.name}`;
    }
    return '';
  };

  useEffect(() => {
    const fetchMenuItems = async () => {
      try {
        const token = localStorage.getItem('authToken');
        const response = await fetch(`${config.API_URL}${config.endpoints.report.getMenus}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.errorCode === "1" && data.data) {
            setMenuItems(data.data);
          }
        }
      } catch (error) {
        console.error('Error fetching menu items:', error);
      }
    };

    if (isAuthenticated) {
      fetchMenuItems();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    const checkLoginTime = () => {
      const loginTime = localStorage.getItem('loginTime');
      if (loginTime && (Date.now() - loginTime > 8 * 60 * 60 * 1000)) {
        // Nếu đã hơn 60 giây
        localStorage.removeItem('authToken');
        localStorage.removeItem('userInfo');
        localStorage.removeItem('loginTime'); // Xóa loginTime
        setIsAuthenticated(false);
        setUserInfo(null);
      }
    };

    checkLoginTime();
  }, []);

  if (isLoading) {
    return <div>Loading...</div>; // hoặc một loading spinner
  }

  if (!isAuthenticated) {
    return <Login onLogin={handleLogin} />;
  }

  return (
    <div className="app-container">
      <header className="app-header">
        <div className="header-left">
          <div className="logo-container" onClick={handleLogoClick} style={{ cursor: 'pointer' }}>
            <img 
              src="/zalopay_logo/Logo FA-11.png"
              alt="ZaloPay Logo"
              height="40"
            />
            <span className="logo-text">Ads Report</span>
          </div>
          <nav className="main-nav">
            {Object.entries(menuItems).map(([category, items]) => (
              <div className="nav-item dropdown" key={category}>
                <button className="dropdown-trigger">
                  {category}
                  <i className="fas fa-chevron-down"></i>
                </button>
                <ul className="dropdown-menu">
                  {items.map((item) => (
                    <li 
                      key={item.dashboardId}
                      onClick={() => handleDashboardClick(item.dashboardId)}
                      className={activeDashboard === item.dashboardId ? 'active' : ''}
                    >
                      {item.name}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>
        </div>

        {/* {activeDashboard && (
          <div 
            className="dashboard-title" 
            style={{ color: isDarkMode ? '#00CF6A' : '#0033C9' }}
          >
            {getDashboardFullName(activeDashboard)}
          </div>
        )} */}
        
        {userInfo && (
          <UserMenu 
            username={userInfo.username}
            onLogout={handleLogout}
            isDarkMode={isDarkMode}
            onToggleTheme={toggleTheme}
          />
        )}
      </header>

      <main className="main-content">
        {!activeDashboard && <Welcome />}
        <Dashboard url={currentDashboardUrl} />
        
        {/* Error Modal */}
        {errorMessage && (
          <div className="modal-overlay">
            <div className="modal-content">
              <div className="modal-icon">
                <i className="fas fa-exclamation-circle"></i>
              </div>
              <div className="modal-message">
                {errorMessage}
              </div>
              <button 
                className="modal-button" 
                onClick={handleCloseError}
                autoFocus
              >
                OK
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default App;
